import React, { useCallback, useEffect, useMemo, useState } from "react";
import API from "../services/api";
import useI18n from "../i18n/useI18n";

const THEME_KEY = "hookora_theme";
const LANGUAGE_KEY = "hookora_language";

const FALLBACKS = {
  en: {
    waitlistTitle: "Get notified when billing goes live",
    waitlistDescription:
      "Join the waitlist for paid plans and early billing access. This helps us know which users want Creator or Pro first.",
    currentPlan: "Current plan",
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    interestedInLabel: "Interested in",
    bothPlans: "Both",
    notesLabel: "Notes",
    notesPlaceholder: "Tell us what you’d want most from paid plans...",
    submitting: "Submitting...",
    joinBillingWaitlist: "Join Billing Waitlist",
    waitlistSuccess: "Added to the billing waitlist successfully.",
    waitlistError: "Failed to join the billing waitlist.",
    free: "Free",
    creator: "Creator",
    pro: "Pro",
  },
  pt: {
    waitlistTitle: "Receba aviso quando a cobrança entrar no ar",
    waitlistDescription:
      "Entre na lista de espera para planos pagos e acesso antecipado à cobrança. Isso nos ajuda a saber quais usuários querem Creator ou Pro primeiro.",
    currentPlan: "Plano atual",
    emailLabel: "E-mail",
    emailPlaceholder: "voce@exemplo.com",
    interestedInLabel: "Interesse em",
    bothPlans: "Ambos",
    notesLabel: "Notas",
    notesPlaceholder: "Conte para nós o que você mais gostaria de ver nos planos pagos...",
    submitting: "Enviando...",
    joinBillingWaitlist: "Entrar na lista de espera da cobrança",
    waitlistSuccess: "Você entrou na lista de espera da cobrança com sucesso.",
    waitlistError: "Falha ao entrar na lista de espera da cobrança.",
    free: "Free",
    creator: "Creator",
    pro: "Pro",
  },
  fr: {
    waitlistTitle: "Soyez averti lorsque la facturation sera disponible",
    waitlistDescription:
      "Rejoignez la liste d’attente pour les offres payantes et l’accès anticipé à la facturation. Cela nous aide à savoir quels utilisateurs veulent Creator ou Pro en premier.",
    currentPlan: "Forfait actuel",
    emailLabel: "E-mail",
    emailPlaceholder: "vous@exemple.com",
    interestedInLabel: "Intéressé par",
    bothPlans: "Les deux",
    notesLabel: "Notes",
    notesPlaceholder: "Dites-nous ce que vous aimeriez le plus dans les offres payantes...",
    submitting: "Envoi...",
    joinBillingWaitlist: "Rejoindre la liste d’attente de facturation",
    waitlistSuccess: "Ajouté à la liste d’attente de facturation avec succès.",
    waitlistError: "Impossible de rejoindre la liste d’attente de facturation.",
    free: "Free",
    creator: "Creator",
    pro: "Pro",
  },
  es: {
    waitlistTitle: "Recibe una notificación cuando la facturación esté disponible",
    waitlistDescription:
      "Únete a la lista de espera para los planes pagos y el acceso anticipado a la facturación. Esto nos ayuda a saber qué usuarios quieren Creator o Pro primero.",
    currentPlan: "Plan actual",
    emailLabel: "Correo electrónico",
    emailPlaceholder: "tu@ejemplo.com",
    interestedInLabel: "Interesado en",
    bothPlans: "Ambos",
    notesLabel: "Notas",
    notesPlaceholder: "Cuéntanos qué te gustaría más de los planes pagos...",
    submitting: "Enviando...",
    joinBillingWaitlist: "Unirse a la lista de espera de facturación",
    waitlistSuccess: "Agregado correctamente a la lista de espera de facturación.",
    waitlistError: "No se pudo unir a la lista de espera de facturación.",
    free: "Free",
    creator: "Creator",
    pro: "Pro",
  },
};

const BillingInterestForm = () => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const getStoredLanguage = () => {
    if (typeof window === "undefined") return "en";
    return (
      localStorage.getItem(LANGUAGE_KEY) ||
      document.documentElement.lang ||
      "en"
    )
      .toLowerCase()
      .slice(0, 2);
  };

  const [email, setEmail] = useState("");
  const [interestedIn, setInterestedIn] = useState("creator");
  const [notes, setNotes] = useState("");
  const [currentPlan, setCurrentPlan] = useState("free");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [theme, setTheme] = useState(getStoredTheme());
  const [language, setLanguage] = useState(getStoredLanguage());

  const tt = useCallback(
    (key, fallbackKey, englishFallback) => {
      const value = t(key);
      if (value !== key) return value;

      const langPack = FALLBACKS[language] || FALLBACKS.en;
      return langPack[fallbackKey] || englishFallback;
    },
    [t, language]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    const syncLanguage = () => {
      setLanguage(getStoredLanguage());
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);

    window.addEventListener("storage", syncLanguage);
    window.addEventListener("hookora-preferences-updated", syncLanguage);
    window.addEventListener("hookora-language-change", syncLanguage);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);

      window.removeEventListener("storage", syncLanguage);
      window.removeEventListener("hookora-preferences-updated", syncLanguage);
      window.removeEventListener("hookora-language-change", syncLanguage);
    };
  }, []);

  useEffect(() => {
    API.get("/users/profile")
      .then((res) => {
        setEmail(res.data.email || "");
        setCurrentPlan(res.data.plan || "free");
      })
      .catch((err) => {
        console.error("Failed to load profile for waitlist form:", err);
      });
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      textMuted: isDark ? "#94a3b8" : "#4b5563",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 8px 24px rgba(15, 23, 42, 0.04)",
      planFreeBg: isDark ? "rgba(120,53,15,0.24)" : "#fffbeb",
      planFreeBorder: isDark ? "rgba(251,191,36,0.34)" : "#fde68a",
      planPaidBg: isDark ? "rgba(8,47,73,0.28)" : "#ecfeff",
      planPaidBorder: isDark ? "rgba(34,211,238,0.32)" : "#a5f3fc",
      successBg: isDark ? "rgba(20,83,45,0.30)" : "#f0fdf4",
      successBorder: isDark ? "rgba(74,222,128,0.30)" : "#bbf7d0",
      successText: isDark ? "#bbf7d0" : "#166534",
      errorBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.35)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText,
  };

  const formatPlanName = useCallback(
    (plan) => {
      if (!plan || plan === "free") {
        return tt("plans.free", "free", "Free");
      }
      if (plan === "creator") {
        return tt("plans.creator", "creator", "Creator");
      }
      if (plan === "pro") {
        return tt("plans.pro", "pro", "Pro");
      }
      return plan.charAt(0).toUpperCase() + plan.slice(1);
    },
    [tt]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setSuccess("");
      setError("");

      const response = await API.post("/waitlist/interest", {
        email: email.trim(),
        interested_in: interestedIn,
        notes: notes.trim(),
      });

      setSuccess(
        response.data.message ||
          tt(
            "pricing.waitlistSuccess",
            "waitlistSuccess",
            "Added to the billing waitlist successfully."
          )
      );
      setNotes("");
    } catch (err) {
      console.error("Failed to submit billing interest:", err);
      setError(
        err?.response?.data?.detail ||
          tt(
            "pricing.waitlistError",
            "waitlistError",
            "Failed to join the billing waitlist."
          )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        marginTop: "28px",
        background: colors.cardBg,
        border: `1px solid ${colors.border}`,
        borderRadius: "18px",
        padding: "22px",
        boxShadow: colors.shadow,
        backdropFilter: isDark ? "blur(14px)" : "none",
      }}
    >
      <h3 style={{ marginTop: 0, color: colors.text }}>
        {tt(
          "pricing.waitlistTitle",
          "waitlistTitle",
          "Get notified when billing goes live"
        )}
      </h3>

      <p style={{ color: colors.textMuted, lineHeight: "1.7" }}>
        {tt(
          "pricing.waitlistDescription",
          "waitlistDescription",
          "Join the waitlist for paid plans and early billing access. This helps us know which users want Creator or Pro first."
        )}
      </p>

      <div
        style={{
          marginBottom: "16px",
          background: currentPlan === "free" ? colors.planFreeBg : colors.planPaidBg,
          border: `1px solid ${
            currentPlan === "free" ? colors.planFreeBorder : colors.planPaidBorder
          }`,
          borderRadius: "14px",
          padding: "12px 14px",
          color: colors.textSoft,
          fontSize: "14px",
        }}
      >
        {tt("pricing.currentPlan", "currentPlan", "Current plan")}:{" "}
        <strong>{formatPlanName(currentPlan)}</strong>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: "grid", gap: "12px" }}>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: colors.textSoft,
                fontWeight: "600",
              }}
            >
              {tt("pricing.emailLabel", "emailLabel", "Email")}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={tt(
                "pricing.emailPlaceholder",
                "emailPlaceholder",
                "you@example.com"
              )}
              style={inputStyle}
              required
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: colors.textSoft,
                fontWeight: "600",
              }}
            >
              {tt(
                "pricing.interestedInLabel",
                "interestedInLabel",
                "Interested in"
              )}
            </label>
            <select
              value={interestedIn}
              onChange={(e) => setInterestedIn(e.target.value)}
              style={inputStyle}
            >
              <option value="creator">
                {tt("plans.creator", "creator", "Creator")}
              </option>
              <option value="pro">
                {tt("plans.pro", "pro", "Pro")}
              </option>
              <option value="both">
                {tt("pricing.bothPlans", "bothPlans", "Both")}
              </option>
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: colors.textSoft,
                fontWeight: "600",
              }}
            >
              {tt("pricing.notesLabel", "notesLabel", "Notes")}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={tt(
                "pricing.notesPlaceholder",
                "notesPlaceholder",
                "Tell us what you’d want most from paid plans..."
              )}
              rows={4}
              style={{
                ...inputStyle,
                resize: "vertical",
              }}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            marginTop: "16px",
            padding: "14px 16px",
            borderRadius: "12px",
            border: "none",
            background: loading ? "#9ca3af" : "linear-gradient(135deg, #2563eb, #7c3aed)",
            color: "#fff",
            fontWeight: "700",
            cursor: loading ? "not-allowed" : "pointer",
            width: "100%",
          }}
        >
          {loading
            ? tt("pricing.submitting", "submitting", "Submitting...")
            : tt(
                "pricing.joinBillingWaitlist",
                "joinBillingWaitlist",
                "Join Billing Waitlist"
              )}
        </button>
      </form>

      {success && (
        <div
          style={{
            marginTop: "14px",
            background: colors.successBg,
            border: `1px solid ${colors.successBorder}`,
            color: colors.successText,
            borderRadius: "12px",
            padding: "12px 14px",
          }}
        >
          {success}
        </div>
      )}

      {error && (
        <div
          style={{
            marginTop: "14px",
            background: colors.errorBg,
            border: `1px solid ${colors.errorBorder}`,
            color: colors.errorText,
            borderRadius: "12px",
            padding: "12px 14px",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default BillingInterestForm;